/**
 * Ce que les CINQ LECTURES du tuteur laissent à la console — chantier « manuel
 * vivant », Φ-MV-5, seconde tranche (MV-TUT-01, 04, 07, 08, 11).
 *
 * Même règle que `manuel-tuteur-format.ts` : l'ordre de la liste, la raison de
 * chaque ligne, les priorités d'une séance, leurs titres, le plan, les verdicts
 * et les paliers sont CALCULÉS ET ÉCRITS PAR LE SERVEUR, et affichés tels
 * quels. On ne trouve ici que ce que le contrat laisse à l'écran :
 *
 *  - reconnaître une ligne SANS DONNÉE PÉDAGOGIQUE (consentement absent) ;
 *  - retenir les lignes d'un filtre ou d'une recherche — sans jamais les
 *    réordonner : l'ordre reçu est l'ordre rendu ;
 *  - dire une date, qu'elle arrive en date civile ou en instant ;
 *  - le RÉSUMÉ d'une section repliée — des dénombrements, jamais un taux ;
 *  - le brouillon de séquence tiré d'une préparation de séance.
 *
 * Invariant tenu ici : aucune fonction ne moyenne ni ne produit de pourcentage
 * (`DECISIONS-ARCHITECTE.md`, point 17 — même pour le tuteur) ; la seule
 * division convertit des secondes en minutes.
 * Fonctions pures : elles s'éprouvent sans rendu (`manuel-tuteur-lectures.test.ts`).
 */
import {
  GENRES_DE_TRACE,
  LIBELLES_GENRE_DE_TRAVAIL,
  LIBELLES_VERDICT_HISTORIQUE,
  MAX_CARACTERES_RAISON_ELEMENT,
  MAX_CARACTERES_TITRE_SEQUENCE,
  MAX_ELEMENTS_SEQUENCE,
  VERDICTS_EXERCICE_MANUEL,
  denombrer,
  estDateCivile,
  type DepuisLaDerniereSeanceDto,
  type EleveSuiviDto,
  type GenreDeGeste,
  type NotionDeRessourceDto,
  type PreparationSeanceResponse,
  type SemaineDEvolutionDto,
  type SignalDePriorite,
  type TravauxTuteurResponse,
} from '@silex/shared';

import {
  ajouterJours,
  cleDElement,
  jourCivil,
  nomDeNiveau,
  type BrouillonSequence,
  type ElementCompose,
} from './manuel-tuteur-format';
import { formatDate } from './tuteur-format';

/* ─────────────────────────── la liste par « pourquoi maintenant » ─────────────────────────── */

/**
 * Une ligne de la liste, telle que l'écran la lit : soit la ligne complète du
 * contrat, soit une ligne SANS SUIVI — l'élève est rattaché, mais le parent n'a
 * pas (ou plus) donné le consentement qui ouvre ses données pédagogiques.
 */
export type LigneDeListe =
  | {
      readonly suivi: true;
      readonly eleve: { readonly id: string; readonly prenom: string };
      readonly donnees: EleveSuiviDto;
    }
  | {
      readonly suivi: false;
      readonly eleve: { readonly id: string; readonly prenom: string };
      readonly ordre: number | null;
    };

/** Ce que la ligne dit quand le suivi n'est pas autorisé. Sobre : un fait, sans reproche. */
export const PHRASE_SUIVI_NON_AUTORISE =
  'Suivi non autorisé : le parent n’a pas donné son consentement. Aucune donnée de travail n’est affichée.';

/**
 * Lit une ligne DÉFENSIVEMENT. Le serveur peut la rendre sans donnée
 * pédagogique — un champ explicite (`consentement: false`), ou simplement des
 * champs pédagogiques absents ou nuls. Dans les deux cas l'écran ne suppose
 * rien : ni raison, ni compte, ni évaluation.
 */
export function lireLigne(brut: EleveSuiviDto): LigneDeListe {
  const l = brut as unknown as Record<string, unknown>;
  const sansDonnee =
    l.consentement === false ||
    typeof l.pourquoiMaintenant !== 'string' ||
    l.septDerniersJours === null ||
    typeof l.septDerniersJours !== 'object' ||
    !Array.isArray(l.autresSignaux);
  if (!sansDonnee) return { suivi: true, eleve: brut.eleve, donnees: brut };
  return {
    suivi: false,
    eleve: brut.eleve,
    ordre: typeof l.ordre === 'number' ? l.ordre : null,
  };
}

/** Les signaux qu'une ligne lève : celui qui l'emporte, puis les suivants. */
function signauxDe(ligne: LigneDeListe): readonly SignalDePriorite[] {
  if (!ligne.suivi) return [];
  const { signal, autresSignaux } = ligne.donnees;
  return [...(signal === null ? [] : [signal.code]), ...autresSignaux.map((s) => s.code)];
}

/**
 * Les lignes qui passent le filtre et la recherche — DANS L'ORDRE REÇU. Rien
 * n'est trié ici : la priorité appartient au serveur (MV-TUT-01).
 */
export function retenirLignes(
  lignes: readonly LigneDeListe[],
  signal: SignalDePriorite | null,
  recherche: string,
): LigneDeListe[] {
  const q = recherche.trim().toLocaleLowerCase('fr');
  return lignes.filter(
    (l) =>
      (signal === null || signauxDe(l).includes(signal)) &&
      (q === '' || l.eleve.prenom.toLocaleLowerCase('fr').includes(q)),
  );
}

/** « 6 travaux · 4 réussis », ou « Aucun travail ». Deux comptes, jamais l'un sur l'autre. */
export function direSeptJours(s: EleveSuiviDto['septDerniersJours']): string {
  if (s === null || s.travaux === 0) return 'Aucun travail';
  return `${denombrer(s.travaux, 'travail', 'travaux')} · ${denombrer(s.reussis, 'réussi')}`;
}

/** Une séance à venir de la liste, avec l'élève qu'elle concerne. */
export interface SeanceAVenir {
  readonly eleve: { readonly id: string; readonly prenom: string };
  readonly debut: string;
  readonly dureeMin: number;
  /** L'étiquette du signal qui l'emporte, ou « Suivi régulier ». */
  readonly motif: string;
}

/** « Mes prochaines séances » — quatre au plus, la plus proche d'abord (un tri par DATE, pas par priorité). */
export function prochainesSeances(lignes: readonly LigneDeListe[], max = 4): SeanceAVenir[] {
  const seances: SeanceAVenir[] = [];
  for (const l of lignes) {
    if (!l.suivi || l.donnees.prochaineSeance === null) continue;
    seances.push({
      eleve: l.eleve,
      debut: l.donnees.prochaineSeance.debut,
      dureeMin: l.donnees.prochaineSeance.dureeMin,
      motif: l.donnees.signal?.etiquette ?? 'Suivi régulier',
    });
  }
  return seances.sort((a, b) => a.debut.localeCompare(b.debut)).slice(0, max);
}

/* ─────────────────────────── les dates ─────────────────────────── */

/**
 * « 1er octobre » — que le serveur ait rendu une DATE CIVILE (`AAAA-MM-JJ`) ou
 * un instant : la première ne se lit pas dans un fuseau, le second si.
 */
export function direJour(valeur: string): string {
  return estDateCivile(valeur) ? jourCivil(valeur) : formatDate(valeur);
}

/* ─────────────────────────── la préparation de séance ─────────────────────────── */

/**
 * « 2 · Multiplier des puissances » — une notion, sur une page qui ne connaît
 * pas le rang de son chapitre (la préparation ne rend que son code et son
 * titre) : son rang dans le chapitre et son titre, un prérequis par son titre
 * seul. Jamais son code (MV-UX-04).
 */
export function nomCourtDeNotion(notion: NotionDeRessourceDto): string {
  return notion.rang === null ? notion.titre : `${notion.rang} · ${notion.titre}`;
}

/** « 6 travaux · 4 réussis · 35 min » — le résumé de la période, replié. */
export function resumeDepuis(d: DepuisLaDerniereSeanceDto): string {
  if (d.travaux === 0) return 'Aucun travail sur la période';
  return [
    denombrer(d.travaux, 'travail', 'travaux'),
    denombrer(d.reussis, 'réussi'),
    `${d.minutes} min`,
  ].join(' · ');
}

/** « 5 jours de travail sur 14 » — un dénombrement de JOURS. */
export function direJoursActifs(jours: DepuisLaDerniereSeanceDto['jours']): string {
  const actifs = jours.filter((j) => j.actif).length;
  return `${denombrer(actifs, 'jour')} de travail sur ${jours.length}`;
}

const tronquer = (texte: string, max: number): string =>
  texte.length <= max ? texte : `${texte.slice(0, max - 1).trimEnd()}…`;

/**
 * LE BROUILLON DE SÉQUENCE tiré d'une préparation (« Déposer comme séquence »).
 *
 * On ne propose que ce que le serveur a DÉSIGNÉ comme appui de chaque priorité,
 * dans l'ordre des priorités : la capsule, la fiche, puis les exercices. Sans
 * doublon, et pas plus que ce qu'une séquence accepte. La raison de chaque
 * élément est le titre de sa priorité (ou, pour un exercice, le « pourquoi » du
 * serveur). Le tuteur relit, retire, complète : rien n'est déposé d'ici.
 *
 * Titre : « Reprendre : {notion} » quand la première priorité vise une notion,
 * sinon « Séquence de la semaine ». Échéance : la veille de la prochaine
 * évaluation si elle est encore devant nous, sinon dans six jours (MV-TUT-06).
 */
export function brouillonDepuisPreparation(p: PreparationSeanceResponse): BrouillonSequence {
  const elements: ElementCompose[] = [];
  const vus = new Set<string>();
  const ajouter = (e: ElementCompose): void => {
    const cle = cleDElement(e);
    if (vus.has(cle) || elements.length >= MAX_ELEMENTS_SEQUENCE) return;
    vus.add(cle);
    elements.push(e);
  };
  for (const priorite of p.priorites) {
    const raison = tronquer(priorite.titre, MAX_CARACTERES_RAISON_ELEMENT);
    const { capsule, fiche, exercices } = priorite.appui;
    if (capsule !== null) {
      ajouter({
        genre: 'capsule',
        code: capsule.code,
        titre: capsule.titre,
        sorte: 'Capsule',
        raison,
      });
    }
    if (fiche !== null) {
      ajouter({ genre: 'fiche', code: fiche.code, titre: fiche.titre, sorte: 'Fiche', raison });
    }
    for (const x of exercices) {
      ajouter({
        genre: 'exercice',
        code: x.code,
        titre: x.titre,
        sorte: `Exercice · ${nomDeNiveau(x.niveauExercice)}`,
        raison: tronquer(x.pourquoi, MAX_CARACTERES_RAISON_ELEMENT),
      });
    }
  }
  const notion = p.priorites[0]?.notion ?? null;
  const veille = p.evaluation === null ? null : ajouterJours(p.evaluation.evaluation.date, -1);
  return {
    titre: tronquer(
      notion === null ? 'Séquence de la semaine' : `Reprendre : ${notion.titre}`,
      MAX_CARACTERES_TITRE_SEQUENCE,
    ),
    mot: '',
    pourLe: veille !== null && veille >= p.aujourdhui ? veille : ajouterJours(p.aujourdhui, 6),
    elements,
  };
}

const GENRES_DE_GESTE: readonly GenreDeGeste[] = ['exercice', 'fiche', 'capsule'];

/**
 * Relit le brouillon qu'une autre page a TRANSMIS par l'état de navigation.
 * L'état d'une entrée d'historique survit au rechargement et n'est pas typé :
 * on ne garde que ce qui a exactement la forme d'un brouillon, sinon `null`
 * (la composition s'ouvre alors vide, comme avant).
 */
export function lireBrouillonTransmis(etat: unknown): BrouillonSequence | null {
  if (typeof etat !== 'object' || etat === null) return null;
  const b = (etat as { brouillon?: unknown }).brouillon;
  if (typeof b !== 'object' || b === null) return null;
  const { titre, mot, pourLe, elements } = b as Record<string, unknown>;
  if (typeof titre !== 'string' || typeof mot !== 'string' || typeof pourLe !== 'string') {
    return null;
  }
  if (!Array.isArray(elements)) return null;
  const lus: ElementCompose[] = [];
  for (const e of elements as unknown[]) {
    if (typeof e !== 'object' || e === null) return null;
    const x = e as Record<string, unknown>;
    const genre = GENRES_DE_GESTE.find((g) => g === x.genre);
    if (
      genre === undefined ||
      typeof x.code !== 'string' ||
      typeof x.titre !== 'string' ||
      typeof x.sorte !== 'string' ||
      typeof x.raison !== 'string'
    ) {
      return null;
    }
    lus.push({ genre, code: x.code, titre: x.titre, sorte: x.sorte, raison: x.raison });
  }
  return { titre, mot, pourLe, elements: lus.slice(0, MAX_ELEMENTS_SEQUENCE) };
}

/* ─────────────────────────── « Travaux et corrections » ─────────────────────────── */

/** Les quatre filtres de l'onglet, tels que l'adresse les porte. `''` : pas de filtre. */
export interface FiltresDeTravaux {
  readonly genre: string;
  readonly notion: string;
  readonly verdict: string;
  readonly erreur: string;
}

export const CLES_DE_FILTRE = ['genre', 'notion', 'verdict', 'erreur'] as const;

/**
 * Les filtres lus de l'adresse. Un genre ou un verdict inconnu est IGNORÉ (le
 * serveur le refuserait d'un 400) ; une notion ou une erreur est un code, que
 * le serveur connaît ou non — il rend alors une liste vide.
 */
export function filtresDeLAdresse(params: URLSearchParams): FiltresDeTravaux {
  const genre = params.get('genre') ?? '';
  const verdict = params.get('verdict') ?? '';
  return {
    genre: GENRES_DE_TRACE.some((g) => g === genre) ? genre : '',
    notion: params.get('notion') ?? '',
    verdict: VERDICTS_EXERCICE_MANUEL.some((v) => v === verdict) ? verdict : '',
    erreur: params.get('erreur') ?? '',
  };
}

export const aucunFiltre = (f: FiltresDeTravaux): boolean =>
  CLES_DE_FILTRE.every((cle) => f[cle] === '');

/** Les choix des deux filtres fermés, dans l'ordre du contrat. */
export const CHOIX_DE_GENRE = GENRES_DE_TRACE.map((g) => ({
  valeur: g,
  libelle: LIBELLES_GENRE_DE_TRAVAIL[g],
}));
export const CHOIX_DE_VERDICT = VERDICTS_EXERCICE_MANUEL.map((v) => ({
  valeur: v,
  libelle: LIBELLES_VERDICT_HISTORIQUE[v],
}));

/** « 12 sur 21 essais, 2 devoirs et 3 défis » — ce qui passe les filtres, et tout le dossier. */
export function direCompteTravaux(r: Pick<TravauxTuteurResponse, 'visibles' | 'comptes'>): string {
  const { essais, devoirs, defis } = r.comptes;
  const dossier = [
    denombrer(essais, 'essai'),
    denombrer(devoirs, 'devoir'),
    ...(defis > 0 ? [denombrer(defis, 'défi')] : []),
  ];
  const dernier = dossier.pop() ?? '';
  return `${r.visibles} sur ${dossier.join(', ')} et ${dernier}`;
}

/** « Aucun indice demandé », « 1 indice sur 2 » ; `null` hors exercice. */
export function direIndices(aides: number | null): string | null {
  if (aides === null) return null;
  return aides === 0 ? 'Aucun indice demandé' : `${denombrer(aides, 'indice')} sur 2`;
}

/** « moins d'une minute », « 4 min » — une durée de travail. */
export function direDureeDeTravail(secondes: number): string {
  if (secondes < 60) return 'moins d’une minute';
  return `${Math.round(secondes / 60)} min`;
}

/* ─────────────────────────── « Évolution » ─────────────────────────── */

/** « 14 travaux en cinq semaines, dont 9 réussis » — deux sommes de COMPTES. */
export function resumeSemaines(semaines: readonly SemaineDEvolutionDto[]): string {
  const travaux = semaines.reduce((n, s) => n + s.travaux, 0);
  const reussis = semaines.reduce((n, s) => n + s.reussis, 0);
  if (travaux === 0) return 'Aucun travail sur la période';
  return `${denombrer(travaux, 'travail', 'travaux')} sur la période, dont ${denombrer(reussis, 'réussi')}`;
}

/**
 * Le rang de la PREMIÈRE SEMAINE À MONTRER (revue UX `UX-7-32`) : celle où
 * l'objectif commence, ou celle du premier travail si elle vient avant — un
 * travail fait n'est jamais retiré de l'écran. Sans objectif (`null`), ou si
 * rien ne permet de trancher, toutes les semaines sont montrées ; la semaine
 * en cours l'est toujours.
 */
export function premiereSemaineUtile(
  semaines: readonly Pick<SemaineDEvolutionDto, 'du' | 'travaux'>[],
  debutObjectif: string | null,
): number {
  if (semaines.length === 0 || debutObjectif === null || !estDateCivile(debutObjectif)) return 0;
  const dernier = semaines.length - 1;
  /* La semaine qui CONTIENT le début de l'objectif : la dernière à commencer au plus tard ce jour-là. */
  let objectif = 0;
  semaines.forEach((s, i) => {
    if (s.du.slice(0, 10) <= debutObjectif) objectif = i;
  });
  const premierTravail = semaines.findIndex((s) => s.travaux > 0);
  return Math.min(dernier, premierTravail === -1 ? objectif : Math.min(objectif, premierTravail));
}

/** « Sem. du 5 oct. » serait un code de plus : on dit « 5 octobre », la semaine en cours « Cette semaine ». */
export function nomDeSemaine(s: Pick<SemaineDEvolutionDto, 'du' | 'enCours'>): string {
  return s.enCours ? 'Cette semaine' : jourCivil(s.du);
}
